<template>
  <div class="cr-panel statement-settle">
    <div class="cr-panel-head">
      <div class="cr-panel-title">
        <fa-icon :icon="['fal', 'check-circle']" class="cr-panel-icon"/>
        Baixa do título
      </div>
      <div class="cr-panel-subtitle">
        Ação explícita: o demonstrativo registra o pagamento, mas só baixa o título se você ligar a
        opção abaixo.
      </div>
    </div>
    <div class="cr-panel-body">
      <label class="form-switch bg-settle-switch">
        <input type="checkbox" v-model="form.enabled" :disabled="readonly || !hasOpen"
               @change="emit">
        <i class="form-icon"></i>
        Baixar título com o demonstrativo
        <span class="text-xs text-gray" v-if="!hasOpen"> (nenhum título aberto para baixar)</span>
      </label>
      <div class="stenci-group stenci-alert bg-settle-box" v-if="form.enabled">
        <div class="columns">
          <div class="column col-5 col-sm-12 form-group"
               :class="{'has-error': validator.bankAccountId.$error}">
            <label class="form-label">Conta bancária</label>
            <select class="form-select" v-model="form.bankAccountId" :disabled="readonly"
                    @change="emit" @blur="validator.bankAccountId.$touch()">
              <option value="">[Selecione]</option>
              <option v-for="account in bankAccounts" :value="account.id" :key="account.id">
                {{ account.name }}
              </option>
            </select>
            <div class="form-input-hint" v-if="validator.bankAccountId.$error">
              Campo obrigatório
            </div>
          </div>
          <div class="column col-3 col-sm-12 form-group"
               :class="{'has-error': validator.settledAt.$error}">
            <label class="form-label">Data da baixa</label>
            <dx-input-date id="settle-date" class="form-input" v-model="form.settledAt"
            :disabled="readonly"
                           @input="emit" @blur="validator.settledAt.$touch()"/>
            <div class="form-input-hint" v-if="validator.settledAt.$error">Data inválida</div>
          </div>
          <div class="column col-4 col-sm-12 form-group">
            <label class="form-label">
              Forma de recebimento <span class="text-gray">(opcional)</span>
            </label>
            <select class="form-select" v-model="form.methodId" :disabled="readonly" @change="emit">
              <option value="">[Padrão do sistema]</option>
              <option v-for="method in paymentMethods" :value="method.id" :key="method.id">
                {{ method.name }}
              </option>
            </select>
          </div>
        </div>
        <div class="bg-settle-totals">
          <div v-for="line in lines"
               :key="String(line.financialId || line.batchId) + line.tipoPagamento">
            <span>
              {{ line.operation === 'recovery' ? 'Recuperação' : 'Título' }}
              {{ line.document || `LOTE ${line.number}` }}
              <span class="text-xs text-gray">· pago em {{ line.paymentDate | date }}</span>
            </span>
            <span class="tnum">
              {{ settleValue(line) | currency }}
              <span class="text-xs text-gray" v-if="num(line.paid) !== settleValue(line)">
                · operadora informou {{ line.paid | currency }}
              </span>
            </span>
          </div>
          <div v-if="debits > 0">
            <span>
              Débitos lançados como desconto <span class="text-xs text-gray">(
              {{ debitsHint }})</span>
            </span>
            <span class="tnum text-red">−{{ debits | currency }}</span>
          </div>
          <div v-if="credits > 0">
            <span>Créditos da operadora</span>
            <span class="tnum text-green">+{{ credits | currency }}</span>
          </div>
          <div class="bg-settle-net">
            <span><b>Recebido na conta</b></span>
            <span class="tnum bg-settle-big">{{ net | currency }}</span>
          </div>
        </div>
        <div class="text-xs text-gray mt-1">
          Os débitos aparecem na conciliação bancária como desconto do título. O repasse dos
          profissionais não muda com a baixa.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getName as debitTypeName } from '@/data/tiss-payment-debit-types';
import { num, round2 } from '../helpers';

export default {
  props: {
    value: {
      type: Object,
      required: true,
    },
    validator: {
      type: Object,
      required: true,
    },
    receivables: {
      type: Array,
      default: () => [],
    },
    payments: {
      type: Array,
      default: () => [],
    },
    bankAccounts: {
      type: Array,
      default: () => [],
    },
    paymentMethods: {
      type: Array,
      default: () => [],
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: { ...this.value },
    };
  },
  computed: {
    openLines() {
      return this.receivables.filter(line => line.status === 'open');
    },
    hasOpen() {
      return this.openLines.length > 0;
    },
    lines() {
      return this.openLines;
    },
    debits() {
      return round2(this.openLines.reduce((sum, line) => sum + num(line.debits), 0));
    },
    credits() {
      return round2(this.openLines.reduce((sum, line) => sum + num(line.credits), 0));
    },
    net() {
      return round2(this.openLines.reduce((sum, line) => (
        sum + this.settleValue(line) - num(line.debits) + num(line.credits)
      ), 0));
    },
    debitsHint() {
      const names = new Set();
      this.payments.forEach(payment => (payment.debits || []).forEach((debit) => {
        const name = debitTypeName(debit.type);
        if (name) names.add(name);
      }));
      return [...names].join(' · ') || 'impostos retidos';
    },
  },
  watch: {
    value: {
      deep: true,
      handler(value) {
        this.form = { ...value };
      },
    },
  },
  methods: {
    num,
    settleValue(line) {
      return line.status === 'open' && num(line.value) > 0 ? num(line.value) : num(line.paid);
    },
    emit() {
      this.$emit('input', { ...this.form });
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/_variables.scss";

.statement-settle {
  .bg-settle-switch { font-weight: 600; margin: 0; }
  .bg-settle-box { margin-bottom: 0; margin-top: $layout-spacing; }
  .bg-settle-totals {
    border-top: #F2C94C solid $border-width;
    display: flex;
    flex-direction: column;
    font-size: $font-size-sm;
    gap: .2rem;
    margin-top: $layout-spacing;
    padding-top: $layout-spacing;
    > div { display: flex; gap: $layout-spacing; justify-content: space-between; }
    .bg-settle-net { margin-top: .1rem; }
    .bg-settle-big { color: #1F7A4E; font-size: $font-size-lg; font-weight: 700; }
  }
}
</style>
